import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, HelpCircle, Menu, Moon, Search, Settings, Sun, User } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "../ui/sheet";
import { Badge } from "../ui/badge";
import { useTheme } from "../../context/ThemeContext";
import { useConsultation } from "../../context/ConsultationContext";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const demoLinks = [
  { path: "/demo/receptionist", label: "AI Receptionist" },
  { path: "/demo/crm", label: "CRM" },
  { path: "/demo/appointments", label: "Booking" },
  { path: "/demo/invoicing", label: "Invoicing" },
  { path: "/demo/inventory", label: "Inventory" },
  { path: "/demo/hr", label: "HR" },
  { path: "/demo/analytics", label: "Analytics" },
];

export const Header = () => {
  const { theme, toggle } = useTheme();
  const { openWith } = useConsultation();
  const nav = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const runSearch = (query) => {
    const cleaned = query.trim().toLowerCase();
    if (!cleaned) return;
    const hit =
      demoLinks.find((d) => d.label.toLowerCase().includes(cleaned)) ||
      demoLinks.find((d) => d.path.toLowerCase().includes(cleaned));
    if (hit) {
      nav(hit.path);
      setQ("");
      setMobileOpen(false);
    } else {
      toast(`No demo matches "${query}"`, { description: "Try: AI, CRM, Booking, Invoicing, Inventory, HR, Analytics" });
    }
  };
  const onSearch = (e) => { e.preventDefault(); runSearch(q); };

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-2 sm:gap-3 lg:gap-4">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="brand-home-link">
          <img
            src="/logo-icon.png"
            alt="StaffLink Ghana"
            className="h-11 sm:h-14 lg:h-16 w-auto object-contain"
          />
          <div className="leading-tight hidden sm:block">
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">SLG Demo Centre</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {demoLinks.slice(0, 5).map((d) => (
            <Link
              key={d.path}
              to={d.path}
              className="text-sm px-3 py-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid={`nav-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {d.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="nav-more">More</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {demoLinks.slice(5).map((d) => (
                <DropdownMenuItem key={d.path} onClick={() => nav(d.path)}>{d.label}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Desktop search */}
        <form onSubmit={onSearch} className="ml-auto hidden md:flex items-center relative w-56 lg:w-64">
          <Search className="w-4 h-4 absolute left-3 text-muted-foreground" />
          <Input
            data-testid="global-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search demos…"
            className="pl-9 h-9 bg-muted/50"
          />
        </form>

        {/* Desktop-only icon buttons */}
        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme" className="hidden md:inline-flex shrink-0">
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="hidden md:inline-flex relative" data-testid="notifications-btn" aria-label="Notifications">
              <Bell className="w-4 h-4" />
              <Badge className="absolute -top-1 -right-1 h-4 min-w-4 px-1 bg-accent text-accent-foreground">3</Badge>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel>Notifications</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="flex-col items-start gap-0.5">
              <span className="text-sm font-medium">New lead: Adjoa Smith</span>
              <span className="text-xs text-muted-foreground">Interested in CRM automation</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="flex-col items-start gap-0.5">
              <span className="text-sm font-medium">Low stock: Nasco Kettle</span>
              <span className="text-xs text-muted-foreground">6 units left</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="flex-col items-start gap-0.5">
              <span className="text-sm font-medium">Onboarding: Yaw Boadu</span>
              <span className="text-xs text-muted-foreground">3 tasks pending</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="hidden md:inline-flex" data-testid="profile-btn" aria-label="Profile">
              <User className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Ama Boateng · Admin</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/settings")}><Settings className="w-4 h-4 mr-2"/> Settings</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/help")}><HelpCircle className="w-4 h-4 mr-2"/> Help centre</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/contact")}>Contact StaffLink</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          className="hidden md:inline-flex bg-primary hover:bg-primary/90"
          onClick={() => openWith({})}
          data-testid="header-book-consultation"
        >
          Book Consultation
        </Button>

        {/* Mobile — everything collapses into the Sheet */}
        <div className="ml-auto md:hidden flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="theme-toggle-mobile"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] sm:w-[380px] flex flex-col p-0" data-testid="mobile-menu-sheet">
              <SheetHeader className="p-5 border-b border-border">
                <SheetTitle className="flex items-center gap-3 text-left">
                  <img src="/logo-icon.png" alt="StaffLink Ghana" className="h-10 w-auto object-contain" />
                  <div className="leading-tight">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">SLG Demo Centre</div>
                    <div className="text-sm font-normal text-muted-foreground">Business automation demos</div>
                  </div>
                </SheetTitle>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {/* Mobile search */}
                <form onSubmit={onSearch} className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="mobile-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search demos…"
                    className="pl-9 h-10 bg-muted/50"
                  />
                </form>

                {/* Mobile nav */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Demos</div>
                  <div className="flex flex-col">
                    {demoLinks.map((d) => (
                      <SheetClose asChild key={d.path}>
                        <Link
                          to={d.path}
                          className="text-sm py-2.5 border-b border-border/60 last:border-none hover:text-primary transition-colors"
                          data-testid={`mobile-nav-${d.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {d.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                </div>

                {/* Quick links */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Support</div>
                  <div className="flex flex-col">
                    <SheetClose asChild>
                      <Link to="/help" className="text-sm py-2.5 border-b border-border/60 flex items-center gap-2" data-testid="mobile-nav-help">
                        <HelpCircle className="w-4 h-4"/> Help centre
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/contact" className="text-sm py-2.5 border-b border-border/60 flex items-center gap-2" data-testid="mobile-nav-contact">
                        <Bell className="w-4 h-4"/> Contact StaffLink
                      </Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <Link to="/settings" className="text-sm py-2.5 flex items-center gap-2" data-testid="mobile-nav-settings">
                        <Settings className="w-4 h-4"/> Settings
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </div>

              {/* Sticky CTA at bottom */}
              <div className="p-5 border-t border-border">
                <Button
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                  onClick={() => { setMobileOpen(false); openWith({}); }}
                  data-testid="mobile-book-consultation"
                >
                  Book Free Consultation
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
